import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService, type DashboardSiteBilling } from '@/api/apiService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'react-hot-toast';

type Props = {
  siteId: number | null;
  billing: DashboardSiteBilling | null;
  isPlatformAdmin: boolean;
  onRefresh: () => Promise<void>;
};

function money(amount: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(amount);
}

function autoRechargeDefaults(billing: DashboardSiteBilling | null | undefined) {
  return {
    enabled: Boolean(billing?.auto_recharge_enabled),
    threshold: String(billing?.auto_recharge_threshold_usd ?? 5),
    chargeAmount: String(billing?.auto_recharge_amount_usd ?? 25),
  };
}

function sameMoneyInput(a: string, b: string): boolean {
  const left = Number(a);
  const right = Number(b);
  if (Number.isFinite(left) && Number.isFinite(right)) {
    return left === right;
  }
  return a.trim() === b.trim();
}

export default function DashboardBillingCard({ siteId, billing, isPlatformAdmin, onRefresh }: Props) {
  const savedAuto = autoRechargeDefaults(billing);
  const [topUpAmount, setTopUpAmount] = useState('25');
  const [autoEnabled, setAutoEnabled] = useState(savedAuto.enabled);
  const [autoThreshold, setAutoThreshold] = useState(savedAuto.threshold);
  const [autoChargeAmount, setAutoChargeAmount] = useState(savedAuto.chargeAmount);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const next = autoRechargeDefaults(billing);
    setAutoEnabled(next.enabled);
    setAutoThreshold(next.threshold);
    setAutoChargeAmount(next.chargeAmount);
  }, [
    billing?.auto_recharge_enabled,
    billing?.auto_recharge_threshold_usd,
    billing?.auto_recharge_amount_usd,
  ]);

  const isAccountScope = billing?.scope === 'account' || (!siteId && Boolean(billing));

  if (!billing) {
    return null;
  }

  if (!isAccountScope && !siteId) {
    return null;
  }

  if (!billing.requires_prepaid_balance && !isPlatformAdmin) {
    return null;
  }

  const paymentRequired = billing.payment_required;
  const autoRechargeDirty =
    autoEnabled !== savedAuto.enabled ||
    !sameMoneyInput(autoThreshold, savedAuto.threshold) ||
    !sameMoneyInput(autoChargeAmount, savedAuto.chargeAmount);
  const canSaveAutoRecharge =
    autoRechargeDirty && !(autoEnabled && !billing.has_payment_method);

  const handleTopUp = async () => {
    setBusy(true);
    try {
      const amount = Number(topUpAmount);
      const session = isAccountScope
        ? await apiService.dashboardCreateAccountBillingCheckout({
            amount_usd: amount,
            save_payment_method: autoEnabled,
          })
        : await apiService.dashboardCreateBillingCheckout(siteId as number, {
            amount_usd: amount,
            save_payment_method: autoEnabled,
          });
      if (session.checkout_url) {
        window.location.href = session.checkout_url;
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveAutoRecharge = async () => {
    if (!autoRechargeDirty) {
      return;
    }
    if (autoEnabled && !billing.has_payment_method) {
      toast.error('Save a payment method before enabling auto-recharge');
      return;
    }
    setBusy(true);
    try {
      if (isAccountScope) {
        await apiService.dashboardUpdateAccountAutoRecharge({
          enabled: autoEnabled,
          threshold_usd: Number(autoThreshold),
          charge_amount_usd: Number(autoChargeAmount),
        });
      } else {
        await apiService.dashboardUpdateAutoRecharge(siteId as number, {
          enabled: autoEnabled,
          threshold_usd: Number(autoThreshold),
          charge_amount_usd: Number(autoChargeAmount),
        });
      }
      toast.success('Auto-recharge settings saved');
      await onRefresh();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to save auto-recharge');
    } finally {
      setBusy(false);
    }
  };

  const handleSavePaymentMethod = async () => {
    setBusy(true);
    try {
      const session = isAccountScope
        ? await apiService.dashboardSetupAccountPaymentMethod()
        : await apiService.dashboardSetupPaymentMethod(siteId as number);
      if (session.checkout_url) {
        toast.loading('Opening Stripe to save your card (no charge)…', { duration: 2500 });
        window.location.href = session.checkout_url;
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to open payment setup');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className={paymentRequired ? 'border-amber-500/60 bg-amber-500/5' : undefined}>
      <CardHeader>
        <CardTitle>Prepaid balance</CardTitle>
        <CardDescription>
          {isPlatformAdmin && !billing.requires_prepaid_balance
            ? 'This site is billing-exempt (owner is a platform admin).'
            : isAccountScope
              ? paymentRequired
                ? 'Add funds here, then claim and crawl your website from Content. Balance moves to your site after the first claim.'
                : 'Account balance — transferred to your first site when you claim it.'
              : paymentRequired
                ? 'Add at least $1 to re-enable the chatbot on your website.'
                : 'Usage (chat + crawl) is deducted from this balance.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {billing.stripe_test_mode ? (
          <div className="rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-sm text-sky-800 dark:text-sky-200">
            <p className="font-medium">Stripe test mode (demo money)</p>
            <p className="mt-1 text-xs opacity-90">
              No real charges. Use card <span className="font-mono">4242 4242 4242 4242</span>, any future
              expiry, any CVC. Funds credit your prepaid balance and spend like production.
            </p>
          </div>
        ) : null}

        {isPlatformAdmin &&
        billing.requires_prepaid_balance &&
        billing.stripe_configured === false ? (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            Stripe is not configured (<span className="font-mono">STRIPE_SECRET_KEY</span>). Top-up is
            unavailable until test or live keys are set.
          </p>
        ) : null}

        <div className="text-2xl font-semibold">
          {money(Number(billing.balance_usd || 0))}
          {billing.hourly_runtime_rate_usd != null && billing.scope === 'site' ? (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              + ${Number(billing.hourly_runtime_rate_usd).toFixed(4)}/hr while running
            </span>
          ) : null}
        </div>

        {isAccountScope && paymentRequired ? (
          <p className="text-sm text-muted-foreground">
            After depositing, go to{' '}
            <Link to="/dashboard/content" className="underline underline-offset-2">
              Content
            </Link>{' '}
            to claim your website and start crawling.
          </p>
        ) : paymentRequired ? (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            Your chatbot is hidden until you add funds.
          </p>
        ) : null}

        {billing.requires_prepaid_balance ? (
          <>
            <div className="space-y-2">
              <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
                <div className="space-y-1">
                  <Label htmlFor="top-up-amount">Top up (min $1)</Label>
                  <Input
                    id="top-up-amount"
                    type="number"
                    min={1}
                    step={1}
                    value={topUpAmount}
                    onChange={(e) => setTopUpAmount(e.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  disabled={busy || billing.stripe_configured === false}
                  onClick={handleTopUp}
                >
                  {billing.stripe_test_mode ? 'Add demo funds' : 'Add funds'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Opens Stripe Checkout to add funds to this balance.
                {autoEnabled && !billing.has_payment_method
                  ? ' With auto-recharge on, that checkout also saves your card for later.'
                  : null}
              </p>
            </div>

            <div className="space-y-3 rounded-md border p-3">
              <div className="space-y-1">
                <p className="text-sm font-medium">Card on file</p>
                <p className="text-xs text-muted-foreground">
                  Needed for auto-recharge. You&apos;ll leave this page for Stripe, enter a card, and return
                  here. Nothing is charged in that step — we only store a card reference for later top-ups.
                </p>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {billing.has_payment_method ? (
                    <p className="text-xs text-muted-foreground">
                      Card saved. You can replace it anytime with the button below.
                    </p>
                  ) : (
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      No card on file yet — auto-recharge can&apos;t run until you add one.
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-1"
                  disabled={busy || billing.stripe_configured === false}
                  onClick={handleSavePaymentMethod}
                >
                  {billing.has_payment_method ? 'Update card on Stripe' : 'Add card on Stripe (no charge)'}
                </Button>
              </div>

              <div className="border-t pt-3 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">Auto-recharge</p>
                    <p className="text-xs text-muted-foreground">
                      When balance falls below the threshold, we charge your saved card for the amount
                      below (min threshold $5, min charge $1). Runs after chat/crawl usage and hourly
                      chatbot runtime billing.
                    </p>
                  </div>
                  <Switch
                    checked={autoEnabled}
                    onCheckedChange={(checked) => {
                      if (checked && !billing.has_payment_method) {
                        toast.error('Add a card on Stripe first, then turn on auto-recharge');
                      }
                      setAutoEnabled(checked);
                    }}
                  />
                </div>
                {autoEnabled ? (
                  <>
                    {!billing.has_payment_method ? (
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        Auto-recharge is on in this form, but it won&apos;t save until a card is on file.
                        Use <span className="font-medium">Add card on Stripe</span> above first.
                      </p>
                    ) : null}
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label htmlFor="auto-threshold">Recharge when below ($)</Label>
                        <Input
                          id="auto-threshold"
                          type="number"
                          min={5}
                          value={autoThreshold}
                          onChange={(e) => setAutoThreshold(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="auto-charge">Charge this amount ($)</Label>
                        <Input
                          id="auto-charge"
                          type="number"
                          min={1}
                          value={autoChargeAmount}
                          onChange={(e) => setAutoChargeAmount(e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                ) : null}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={busy || !canSaveAutoRecharge}
                  onClick={handleSaveAutoRecharge}
                >
                  Save auto-recharge settings
                </Button>
                {!autoRechargeDirty ? (
                  <p className="text-xs text-muted-foreground">No unsaved changes.</p>
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
